import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Search, MessageSquare, Paperclip, Settings2, Plus, LayoutDashboard, AlertTriangle, Inbox, LayoutGrid, PhoneCall, KeyRound, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import api, { formatApiError } from '../../api';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../../components/ui/accordion';
import TicketModal from './TicketModal';
import LogRequestDialog from './LogRequestDialog';

const STATUS_ORDER = [
    'submitted', 'awaiting_information', 'awaiting_client_approval', 'under_review',
    'approved', 'backlog', 'planned', 'in_progress', 'awaiting_client_validation',
    'completed', 'closed', 'declined',
];
const STATUS_LABELS = {
    submitted: 'New', awaiting_information: 'Awaiting Info', awaiting_client_approval: 'Awaiting Approval',
    under_review: 'Under Review', approved: 'Approved', backlog: 'Backlog', planned: 'Planned',
    in_progress: 'In Progress', awaiting_client_validation: 'Awaiting Validation', completed: 'Completed',
    closed: 'Closed', declined: 'Declined',
};
// Simplified board — 12 detailed statuses collapse into 5 visual columns (mirrors support_models.py).
const GROUP_ORDER = ['backlog', 'new', 'with_us', 'waiting_on_client', 'done'];
const GROUP_LABELS = { new: 'New', with_us: 'With Us', waiting_on_client: 'Waiting on Client', backlog: 'Backlog', done: 'Done' };
const STATUS_TO_GROUP = {
    submitted: 'new',
    under_review: 'with_us', approved: 'with_us', planned: 'with_us', in_progress: 'with_us',
    awaiting_information: 'waiting_on_client', awaiting_client_approval: 'waiting_on_client', awaiting_client_validation: 'waiting_on_client',
    backlog: 'backlog',
    completed: 'done', closed: 'done', declined: 'done',
};
const GROUP_DEFAULT_STATUS = { new: 'submitted', with_us: 'in_progress', waiting_on_client: 'awaiting_information', backlog: 'backlog', done: 'completed' };
const PRIORITY_META = {
    critical: { label: 'Critical', dot: '#dc2626' },
    high:     { label: 'High',     dot: '#f59e0b' },
    medium:   { label: 'Medium',   dot: '#3b82f6' },
    low:      { label: 'Low',      dot: '#94a3b8' },
};

function ageDays(createdAt) {
    if (!createdAt) return 0;
    return Math.max(0, Math.floor((Date.now() - new Date(createdAt).getTime()) / (1000 * 60 * 60 * 24)));
}

const TERMINAL_STATUSES = ['completed', 'closed', 'declined'];
function isOverdue(t) {
    if (!t.target_completion_date || TERMINAL_STATUSES.includes(t.status)) return false;
    return t.target_completion_date < new Date().toISOString().slice(0, 10);
}

function BallTag({ ballWith }) {
    return (
        <span className={`text-[9.5px] font-semibold rounded-full px-1.5 py-0.5 ${ballWith === 'client' ? 'bg-amber-100 text-amber-700' : 'bg-teal-100 text-teal-700'}`}>
            {ballWith === 'client' ? 'Client' : 'Us'}
        </span>
    );
}

export default function SupportHubBoard() {
    const { can, user } = useAuth();
    const navigate = useNavigate();
    const canEdit = can('support', 'edit');
    const canDelete = can('support', 'delete');

    const [tickets, setTickets] = useState([]);
    const [platforms, setPlatforms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [platformFilter, setPlatformFilter] = useState('all');
    const [typeFilter, setTypeFilter] = useState('all');
    const [priorityFilter, setPriorityFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    const [assignedToMe, setAssignedToMe] = useState(false);
    const [groupByPlatform, setGroupByPlatform] = useState(false);
    const [openTicketId, setOpenTicketId] = useState(null);
    const [platformsOpen, setPlatformsOpen] = useState(false);
    const [logRequestOpen, setLogRequestOpen] = useState(false);
    const [showAllDone, setShowAllDone] = useState({});
    const DONE_COLLAPSE_LIMIT = 5;

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (search) params.search = search;
            if (platformFilter !== 'all') params.platform = platformFilter;
            if (typeFilter !== 'all') params.request_type = typeFilter;
            if (priorityFilter !== 'all') params.priority = priorityFilter;
            if (statusFilter !== 'all') params.status = statusFilter;
            if (assignedToMe && user?.id) params.assigned_owner_id = user.id;
            const [t, p] = await Promise.all([
                api.get('/support/tickets', { params }),
                api.get('/support/platforms'),
            ]);
            setTickets(t.data.items);
            setPlatforms(p.data.items);
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
        finally { setLoading(false); }
    }, [search, platformFilter, typeFilter, priorityFilter, statusFilter, assignedToMe, user]);
    useEffect(() => { load(); }, [load]);

    const groupTickets = useCallback((list) => {
        const g = {};
        GROUP_ORDER.forEach(k => { g[k] = []; });
        list.forEach(t => { const grp = STATUS_TO_GROUP[t.status] || 'with_us'; g[grp].push(t); });
        // Done column — most recently closed/completed first, so the default
        // "last 5" collapse shows the freshest items, not arbitrary ones.
        g.done.sort((a, b) => (b.completed_at || b.closed_at || b.created_at || '').localeCompare(a.completed_at || a.closed_at || a.created_at || ''));
        return g;
    }, []);

    const byGroup = useMemo(() => groupTickets(tickets), [tickets, groupTickets]);

    const boards = useMemo(() => {
        if (!groupByPlatform) return [{ key: 'all', label: null, byGroup }];
        const codes = [...new Set(tickets.map(t => t.platform))].sort();
        return codes.map(code => ({ key: code, label: platformName(code, platforms), byGroup: groupTickets(tickets.filter(t => t.platform === code)) }));
    }, [groupByPlatform, tickets, byGroup, platforms, groupTickets]);

    const changeGroup = async (ticketId, group) => {
        if (!canEdit) return;
        const newStatus = GROUP_DEFAULT_STATUS[group] || 'in_progress';
        setTickets(prev => prev.map(t => t.id === ticketId ? { ...t, status: newStatus } : t));
        try {
            await api.patch(`/support/tickets/${ticketId}`, { status: newStatus });
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail));
            load();
        }
    };

    const onDragEnd = async (result) => {
        const { source, destination, draggableId } = result;
        if (!destination || !canEdit) return;
        if (destination.droppableId === source.droppableId) return;
        changeGroup(draggableId, destination.droppableId);
    };

    return (
        <div data-testid="support-hub-page" className="bm-fade-in">
            <PageHeader
                title="Support Hub — Board"
                description="Client issue reports and improvement requests across all Beyond Measure platforms."
                actions={
                    <div className="flex items-center gap-2 flex-wrap">
                        <Button variant="outline" onClick={() => navigate('/support-hub')} className="h-9" data-testid="support-inbox-link">
                            <Inbox size={15} className="mr-1.5" /> Needs My Attention
                        </Button>
                        <Button variant="outline" onClick={() => navigate('/support-hub/dashboard')} className="h-9" data-testid="support-dashboard-link">
                            <LayoutDashboard size={15} className="mr-1.5" /> Dashboard
                        </Button>
                        {canEdit && (
                            <Button variant="outline" onClick={() => setLogRequestOpen(true)} className="h-9" data-testid="support-log-request-btn">
                                <PhoneCall size={15} className="mr-1.5" /> Log a request
                            </Button>
                        )}
                        {canEdit && (
                            <Button variant="outline" onClick={() => setPlatformsOpen(true)} className="h-9" data-testid="support-manage-platforms-btn">
                                <Settings2 size={15} className="mr-1.5" /> Manage platforms
                            </Button>
                        )}
                    </div>
                }
            >
                <div className="mt-4 flex items-center gap-2 flex-wrap">
                    <div className="relative w-full sm:w-64">
                        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[color:var(--bm-text-subtle)]" />
                        <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search title, reference, client…"
                               className="h-9 w-full pl-8 bg-white" data-testid="support-search-input" />
                    </div>
                    <Select value={platformFilter} onValueChange={setPlatformFilter}>
                        <SelectTrigger className="h-9 w-[150px] bg-white" data-testid="support-platform-filter"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All platforms</SelectItem>
                            {platforms.map(p => <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <Select value={typeFilter} onValueChange={setTypeFilter}>
                        <SelectTrigger className="h-9 w-[135px] bg-white" data-testid="support-type-filter"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Issue + Improvement + Question</SelectItem>
                            <SelectItem value="issue">Issue</SelectItem>
                            <SelectItem value="improvement">Improvement</SelectItem>
                            <SelectItem value="question">Question</SelectItem>
                        </SelectContent>
                    </Select>
                    <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                        <SelectTrigger className="h-9 w-[125px] bg-white" data-testid="support-priority-filter"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All priority</SelectItem>
                            {Object.entries(PRIORITY_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger className="h-9 w-[150px] bg-white" data-testid="support-status-filter"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All statuses</SelectItem>
                            {STATUS_ORDER.map(s => <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <Button variant={assignedToMe ? 'default' : 'outline'} size="sm" className="h-9"
                            style={assignedToMe ? { background: 'var(--bm-primary)' } : {}}
                            onClick={() => setAssignedToMe(v => !v)} data-testid="support-assigned-to-me-toggle">
                        Assigned to me
                    </Button>
                    <Button variant={groupByPlatform ? 'default' : 'outline'} size="sm" className="h-9"
                            style={groupByPlatform ? { background: 'var(--bm-primary)' } : {}}
                            onClick={() => setGroupByPlatform(v => !v)} data-testid="support-group-by-platform-toggle">
                        <LayoutGrid size={13} className="mr-1" /> Group by platform
                    </Button>
                    <div className="text-[12px] text-[color:var(--bm-text-subtle)] ml-1" data-testid="support-total-count">{tickets.length} request(s)</div>
                </div>
            </PageHeader>

            <div className="px-4 sm:px-8 py-4 sm:py-6 space-y-6">
                {loading ? (
                    <div className="text-[color:var(--bm-text-muted)]">Loading…</div>
                ) : (
                    boards.map(board => (
                        <div key={board.key}>
                            {board.label && <div className="text-[12.5px] font-semibold text-[color:var(--bm-text)] mb-2">{board.label}</div>}

                            {/* Desktop / tablet — full drag-and-drop Kanban */}
                            <div className="hidden md:block">
                            <DragDropContext onDragEnd={onDragEnd}>
                                <div className="flex gap-3 overflow-x-auto pb-2" data-testid={`support-kanban-board-${board.key}`}>
                                    {GROUP_ORDER.map(group => {
                                        const isDone = group === 'done';
                                        const allCards = board.byGroup[group];
                                        const isExpanded = !!showAllDone[board.key];
                                        const visibleCards = isDone && !isExpanded ? allCards.slice(0, DONE_COLLAPSE_LIMIT) : allCards;
                                        return (
                                        <Droppable droppableId={group} key={group}>
                                            {(provided, snapshot) => (
                                                <div ref={provided.innerRef} {...provided.droppableProps}
                                                     className={`flex-1 min-w-[280px] ${isDone ? '' : 'min-h-[65vh]'} rounded-xl border p-2.5 ${snapshot.isDraggingOver ? 'border-[color:var(--bm-accent)] bg-[color:var(--bm-accent)]/5' : 'border-[color:var(--bm-border)] bg-[color:var(--bm-bg-mute)]'}`}
                                                     data-testid={`support-column-${group}`}>
                                                    <div className="flex items-center justify-between px-1 pb-2">
                                                        <div className="text-[12px] font-semibold text-[color:var(--bm-text)]">{GROUP_LABELS[group]}</div>
                                                        <div className="text-[11px] text-[color:var(--bm-text-subtle)] bg-white rounded-full px-1.5 py-0.5">{allCards.length}</div>
                                                    </div>
                                                    <div className="space-y-2 min-h-[40px]">
                                                        {visibleCards.map((t, idx) => (
                                                            <Draggable draggableId={t.id} index={idx} key={t.id} isDragDisabled={!canEdit}>
                                                                {(dragProvided, dragSnapshot) => (
                                                                    <div ref={dragProvided.innerRef} {...dragProvided.draggableProps} {...dragProvided.dragHandleProps}
                                                                         onClick={() => setOpenTicketId(t.id)}
                                                                         className={`bg-white rounded-lg border p-3 cursor-pointer hover:shadow-sm transition ${dragSnapshot.isDragging ? 'shadow-lg' : ''} ${isOverdue(t) ? 'border-rose-300 border-l-[3px] border-l-rose-500' : 'border-[color:var(--bm-border)]'}`}
                                                                         data-testid={`support-card-${t.id}`}>
                                                                        <TicketCardInner t={t} />
                                                                    </div>
                                                                )}
                                                            </Draggable>
                                                        ))}
                                                        {provided.placeholder}
                                                    </div>
                                                    {isDone && allCards.length > DONE_COLLAPSE_LIMIT && (
                                                        <button
                                                            onClick={() => setShowAllDone(prev => ({ ...prev, [board.key]: !isExpanded }))}
                                                            className="w-full mt-2 h-8 text-[11.5px] font-medium text-[color:var(--bm-primary)] hover:bg-white rounded-md transition"
                                                            data-testid={`support-done-toggle-${board.key}`}
                                                        >
                                                            {isExpanded ? 'Show less' : `Show all ${allCards.length}`}
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                        </Droppable>
                                        );
                                    })}
                                </div>
                            </DragDropContext>
                            </div>

                            {/* Mobile — stacked, tap-to-expand list with a status dropdown per card */}
                            <div className="md:hidden">
                                <MobileGroupedList board={board} onOpen={setOpenTicketId} onChangeGroup={changeGroup} canEdit={canEdit} />
                            </div>
                        </div>
                    ))
                )}
            </div>

            <TicketModal
                ticketId={openTicketId}
                onClose={() => setOpenTicketId(null)}
                onChanged={load}
                canEdit={canEdit}
                canDelete={canDelete}
            />
            <ManagePlatformsDialog open={platformsOpen} onClose={() => setPlatformsOpen(false)} onChanged={load} />
            <LogRequestDialog open={logRequestOpen} onClose={() => setLogRequestOpen(false)} onCreated={(id) => { load(); setOpenTicketId(id); }} />
        </div>
    );
}

function TicketCardInner({ t }) {
    const pr = PRIORITY_META[t.priority] || PRIORITY_META.low;
    return (
        <>
            <div className="flex items-center justify-between">
                <span className="text-[10.5px] font-mono text-[color:var(--bm-text-subtle)]">{t.ticket_number}</span>
                <span className="flex items-center gap-1 text-[10px] font-semibold" style={{ color: pr.dot }}>
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: pr.dot }} />{pr.label}
                </span>
            </div>
            <div className="text-[13px] font-medium text-[color:var(--bm-text)] mt-1 line-clamp-2">{t.title}</div>
            <div className="flex items-center gap-1.5 mt-2 text-[10.5px] text-[color:var(--bm-text-subtle)] flex-wrap">
                <span className="bg-[color:var(--bm-bg-mute)] rounded px-1.5 py-0.5">{t.platform}</span>
                <span className={`rounded px-1.5 py-0.5 ${t.request_type === 'issue' ? 'bg-rose-50 text-rose-700' : t.request_type === 'question' ? 'bg-amber-50 text-amber-700' : 'bg-blue-50 text-blue-700'}`}>
                    {t.request_type === 'issue' ? 'Issue' : t.request_type === 'question' ? 'Question' : 'Improvement'}
                </span>
                <span className="rounded px-1.5 py-0.5 bg-slate-100 text-slate-600" data-testid={`support-card-substatus-${t.id}`}>{STATUS_LABELS[t.status]}</span>
                <BallTag ballWith={t.ball_with} />
                {t.origin === 'manual' && (
                    <span className="flex items-center gap-0.5 rounded px-1.5 py-0.5 bg-violet-100 text-violet-700 font-semibold" data-testid={`support-card-manual-${t.id}`}>
                        MANUAL
                    </span>
                )}
                {isOverdue(t) && (
                    <span className="flex items-center gap-0.5 rounded px-1.5 py-0.5 bg-rose-100 text-rose-700 font-semibold" data-testid={`support-card-overdue-${t.id}`}>
                        <AlertTriangle size={9} /> OVERDUE
                    </span>
                )}
            </div>
            <div className="flex items-center justify-between mt-2 text-[11px] text-[color:var(--bm-text-muted)]">
                <span className="truncate">{t.client_name || t.submitted_by_name}</span>
                <div className="flex items-center gap-2 shrink-0">
                    {t.comment_count > 0 && <span className="flex items-center gap-0.5"><MessageSquare size={11} />{t.comment_count}</span>}
                    {t.attachment_count > 0 && <span className="flex items-center gap-0.5"><Paperclip size={11} />{t.attachment_count}</span>}
                    <span>{ageDays(t.created_at)}d</span>
                </div>
            </div>
        </>
    );
}

// Mobile-only stacked list — grouped by the same 5 buckets as the Kanban, but
// swipe/drag is replaced with a simple status dropdown per card (Section 5's
// choice: Kanban on desktop, list view on mobile).
function MobileGroupedList({ board, onOpen, onChangeGroup, canEdit }) {
    return (
        <Accordion type="multiple" defaultValue={['backlog', 'new', 'with_us', 'waiting_on_client']} data-testid={`support-mobile-board-${board.key}`}>
            {GROUP_ORDER.map(group => (
                <AccordionItem key={group} value={group} className="border-[color:var(--bm-border)]">
                    <AccordionTrigger className="text-[13px] font-semibold text-[color:var(--bm-text)] hover:no-underline py-3" data-testid={`support-mobile-group-trigger-${group}`}>
                        <span className="flex items-center gap-2">
                            {GROUP_LABELS[group]}
                            <span className="text-[11px] text-[color:var(--bm-text-subtle)] bg-[color:var(--bm-bg-mute)] rounded-full px-1.5 py-0.5">{board.byGroup[group].length}</span>
                        </span>
                    </AccordionTrigger>
                    <AccordionContent>
                        <div className="space-y-2">
                            {board.byGroup[group].length === 0 && <div className="text-[12px] text-[color:var(--bm-text-subtle)] py-1">No requests here.</div>}
                            {board.byGroup[group].map(t => (
                                <div key={t.id} className={`bg-white rounded-lg border p-3 ${isOverdue(t) ? 'border-rose-300 border-l-[3px] border-l-rose-500' : 'border-[color:var(--bm-border)]'}`}
                                     data-testid={`support-mobile-card-${t.id}`}>
                                    <div onClick={() => onOpen(t.id)} className="cursor-pointer">
                                        <TicketCardInner t={t} />
                                    </div>
                                    {canEdit && (
                                        <div className="mt-2.5 pt-2.5 border-t border-[color:var(--bm-border)]">
                                            <Select key={`${t.id}-${group}`} onValueChange={v => onChangeGroup(t.id, v)}>
                                                <SelectTrigger className="h-8 text-[12px] bg-[color:var(--bm-bg-mute)]" data-testid={`support-mobile-card-move-${t.id}`}>
                                                    <SelectValue placeholder="Move to another status…" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {GROUP_ORDER.filter(g => g !== group).map(g => <SelectItem key={g} value={g}>Move to {GROUP_LABELS[g]}</SelectItem>)}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </AccordionContent>
                </AccordionItem>
            ))}
        </Accordion>
    );
}

function platformName(code, platforms) {
    return (platforms.find(p => p.code === code) || {}).name || code;
}

function ManagePlatformsDialog({ open, onClose, onChanged }) {
    const [items, setItems] = useState([]);
    const [newCode, setNewCode] = useState('');
    const [newName, setNewName] = useState('');
    const [expandedCode, setExpandedCode] = useState(null);
    const [credsForm, setCredsForm] = useState({});
    const [showPassword, setShowPassword] = useState(false);
    const [credsLoading, setCredsLoading] = useState(false);
    const [savingCreds, setSavingCreds] = useState(false);

    const load = useCallback(async () => {
        try { const r = await api.get('/support/platforms'); setItems(r.data.items); }
        catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    }, []);
    useEffect(() => { if (open) load(); }, [open, load]);

    const add = async () => {
        if (!newCode.trim() || !newName.trim()) return toast.error('Enter both a code and a name');
        try {
            await api.post('/support/platforms', { code: newCode.trim().toUpperCase(), name: newName.trim(), active: true });
            setNewCode(''); setNewName('');
            toast.success('Platform added');
            load(); onChanged();
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    };

    const toggle = async (p) => {
        try { await api.patch(`/support/platforms/${p.code}`, { active: !p.active }); load(); onChanged(); }
        catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    };

    const setColor = async (p, color) => {
        setItems(prev => prev.map(x => x.code === p.code ? { ...x, accent_color: color } : x));
        try { await api.patch(`/support/platforms/${p.code}`, { accent_color: color }); }
        catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    };

    const toggleProdLogin = async (p) => {
        if (expandedCode === p.code) { setExpandedCode(null); return; }
        setExpandedCode(p.code);
        setShowPassword(false);
        setCredsLoading(true);
        try {
            const r = await api.get(`/support/platforms/${p.code}`);
            setCredsForm({
                prod_url: r.data.prod_url || '', prod_login_email: r.data.prod_login_email || '',
                prod_login_password: r.data.prod_login_password || '', prod_login_notes: r.data.prod_login_notes || '',
            });
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); setExpandedCode(null); }
        finally { setCredsLoading(false); }
    };

    const saveCreds = async (p) => {
        setSavingCreds(true);
        try {
            await api.patch(`/support/platforms/${p.code}`, credsForm);
            toast.success('Production login saved');
            load(); onChanged();
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
        finally { setSavingCreds(false); }
    };

    const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-[560px]" data-testid="support-platforms-dialog">
                <DialogHeader>
                    <DialogTitle>Manage support platforms</DialogTitle>
                    <DialogDescription className="text-[12.5px] text-[color:var(--bm-text-muted)]">
                        Each platform gets its own submission link. Share it with the platform's dev team to add a "Need Help?" button.
                        Add each platform's production login below so a "Generate fix request" automatically includes it, letting the agent log in and verify the issue and fix live.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-2 max-h-[420px] overflow-y-auto">
                    {items.map(p => (
                        <div key={p.code} className="rounded-md border border-[color:var(--bm-border)]" data-testid={`support-platform-row-${p.code}`}>
                            <div className="flex items-center justify-between px-3 py-2">
                                <div>
                                    <div className="text-[13px] font-medium">{p.name} <span className="text-[11px] text-[color:var(--bm-text-subtle)]">({p.code})</span></div>
                                    <div className="text-[11px] text-[color:var(--bm-primary)] font-mono">{base}/ticket/{p.code}</div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <input type="color" value={p.accent_color || '#0F5F6B'} onChange={e => setColor(p, e.target.value)}
                                           title="Accent colour for this platform's submission form" className="h-8 w-8 rounded cursor-pointer border border-[color:var(--bm-border)]"
                                           data-testid={`support-platform-color-${p.code}`} />
                                    <Button size="sm" variant={p.has_prod_login ? 'default' : 'outline'} style={p.has_prod_login ? { background: 'var(--bm-primary)' } : {}}
                                            onClick={() => toggleProdLogin(p)} data-testid={`support-platform-prod-login-toggle-${p.code}`}>
                                        <KeyRound size={13} className="mr-1" /> Prod login
                                    </Button>
                                    <Button size="sm" variant="outline" onClick={() => toggle(p)} data-testid={`support-platform-toggle-${p.code}`}>
                                        {p.active ? 'Deactivate' : 'Activate'}
                                    </Button>
                                </div>
                            </div>
                            {expandedCode === p.code && (
                                <div className="border-t border-[color:var(--bm-border)] px-3 py-3 bg-[color:var(--bm-bg-mute)] space-y-2" data-testid={`support-platform-prod-login-panel-${p.code}`}>
                                    {credsLoading ? (
                                        <div className="text-[12px] text-[color:var(--bm-text-subtle)]">Loading…</div>
                                    ) : (
                                        <>
                                            <div className="text-[11px] text-[color:var(--bm-text-subtle)]">
                                                Stored so "Generate fix request" can hand the agent a working production login — used to reproduce the issue and verify the fix live.
                                            </div>
                                            <div>
                                                <label className="text-[11px] text-[color:var(--bm-text-muted)]">Production URL</label>
                                                <Input value={credsForm.prod_url} onChange={e => setCredsForm(f => ({ ...f, prod_url: e.target.value }))}
                                                       placeholder="https://client-app.emergent.host" className="h-9 mt-1 bg-white" data-testid={`support-prod-url-${p.code}`} />
                                            </div>
                                            <div className="grid grid-cols-2 gap-2">
                                                <div>
                                                    <label className="text-[11px] text-[color:var(--bm-text-muted)]">Login email</label>
                                                    <Input value={credsForm.prod_login_email} onChange={e => setCredsForm(f => ({ ...f, prod_login_email: e.target.value }))}
                                                           placeholder="admin@client.com" className="h-9 mt-1 bg-white" data-testid={`support-prod-login-email-${p.code}`} />
                                                </div>
                                                <div>
                                                    <label className="text-[11px] text-[color:var(--bm-text-muted)]">Login password</label>
                                                    <div className="relative">
                                                        <Input type={showPassword ? 'text' : 'password'} value={credsForm.prod_login_password}
                                                               onChange={e => setCredsForm(f => ({ ...f, prod_login_password: e.target.value }))}
                                                               placeholder="Password" className="h-9 mt-1 bg-white pr-9" data-testid={`support-prod-login-password-${p.code}`} />
                                                        <button type="button" onClick={() => setShowPassword(v => !v)}
                                                                className="absolute right-2 top-1/2 -translate-y-1/2 mt-0.5 text-[color:var(--bm-text-subtle)]"
                                                                data-testid={`support-prod-login-password-toggle-${p.code}`}>
                                                            {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                            <div>
                                                <label className="text-[11px] text-[color:var(--bm-text-muted)]">Notes (2FA, staging vs prod, anything the agent should know)</label>
                                                <Input value={credsForm.prod_login_notes} onChange={e => setCredsForm(f => ({ ...f, prod_login_notes: e.target.value }))}
                                                       placeholder="e.g. use the 'Support' role account, no 2FA" className="h-9 mt-1 bg-white" data-testid={`support-prod-login-notes-${p.code}`} />
                                            </div>
                                            <div className="flex justify-end">
                                                <Button size="sm" onClick={() => saveCreds(p)} disabled={savingCreds} style={{ background: 'var(--bm-primary)' }}
                                                        data-testid={`support-prod-login-save-${p.code}`}>
                                                    {savingCreds ? 'Saving…' : 'Save production login'}
                                                </Button>
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
                <div className="flex items-end gap-2 pt-2 border-t border-[color:var(--bm-border)]">
                    <div className="flex-1">
                        <label className="text-[11px] text-[color:var(--bm-text-muted)]">Code</label>
                        <Input value={newCode} onChange={e => setNewCode(e.target.value)} placeholder="e.g. XYZ" className="h-9 mt-1" data-testid="support-new-platform-code" />
                    </div>
                    <div className="flex-1">
                        <label className="text-[11px] text-[color:var(--bm-text-muted)]">Name</label>
                        <Input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Platform name" className="h-9 mt-1" data-testid="support-new-platform-name" />
                    </div>
                    <Button onClick={add} className="h-9" style={{ background: 'var(--bm-primary)' }} data-testid="support-new-platform-add-btn"><Plus size={14} className="mr-1" /> Add</Button>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Close</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
